'use client';

import { Divider, Stack, Tab, Tabs, Typography } from '@mui/material';
import { BackupZeroEtlRoutingMode, Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { FormTextField } from '@/components/form/formTextField';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { AllRegionsSelector } from '@/components/selectors/allRegionsSelector';
import { CopyableCode } from '@/components/shared/copyableCode';
import { TextLink } from '@/components/shared/textLink';
import { BackupVaultTagSelection } from '@/components/vaults/backupVaultTagSelection';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';
import { useDAL } from '@/contexts/useDAL';
import { useUser } from '@/contexts/useUser';

import type { ZeroEtlPolicyEditState } from './ZeroEtlPolicyCreationFlow';
import { ZeroEtlDisclaimer } from './zeroEtlDisclaimer';
import { ZeroEtlRouting } from './zeroEtlRouting';

const awsAccountIdConfig = getCloudFieldConfig(Provider.Aws, 'accountId');
const roleArnConfig = getCloudFieldConfig(Provider.Aws, 'roleArn', {
  required: true,
});

export const ZeroEtlPolicyTableDefinition = (
  props: StepProps<ZeroEtlPolicyEditState>
) => {
  const isManualRouting =
    props.wizardState.routingMode ===
    BackupZeroEtlRoutingMode.BACKUP_ZERO_ETL_ROUTING_MODE_MANUAL;

  const isValidAccountId = awsAccountIdConfig.validation.validateInput(
    props.wizardState.catalogAwsAccountId
  );
  const isValidRoleArn = roleArnConfig.validation.validateInput(
    props.wizardState.catalogIamRoleArn
  );

  const canGoNext =
    !!props.wizardState.vaultId &&
    !!props.wizardState.catalogAwsAccountId &&
    isValidAccountId &&
    !!props.wizardState.catalogAwsRegion &&
    isValidRoleArn &&
    (!isManualRouting ||
      (!!props.wizardState.namespaceTemplate &&
        !!props.wizardState.tableTemplate));

  return (
    <StepContainer
      stepperLabels={props.stepperLabels}
      stepperIndex={2}
      onBackClick={props.back}
      canGoNext={() => canGoNext}
      nextButtonDisabledTooltip={
        !props.wizardState.vaultId
          ? 'Select a vault'
          : !props.wizardState.catalogAwsAccountId ||
              !props.wizardState.catalogAwsRegion
            ? 'Select an AWS account and region'
            : !isValidAccountId
              ? 'Enter a valid AWS account ID'
              : !isValidRoleArn
                ? 'Enter a valid IAM Role ARN'
                : 'Enter a namespace and table template for manual routing'
      }
      onNextClick={async () => {
        props.setNextStep(props.currentStep.next?.[0]);
      }}
      sx={{ padding: '0px' }}
      navigationComponent={
        <RenameEntityName
          label='Integration name'
          name={props.wizardState.name}
          onSubmit={(name) =>
            props.setWizardState((state) => ({ ...state, name }))
          }
        />
      }
    >
      <ZeroEtlDisclaimer />
      <Stack
        direction='row'
        justifyContent='space-between'
        className='pt-[min(60px,3vw)]'
        flexGrow={1}
        minHeight={0}
        divider={<Divider orientation='vertical' flexItem />}
      >
        <Stack
          flexGrow={1}
          minHeight={0}
          className='mx-[min(100px,5vw)]'
          gap='40px'
          minWidth={0}
          maxWidth='310px'
          divider={<Divider sx={{ borderStyle: 'dashed' }} />}
        >
          <StorageConfiguration
            vaultId={props.wizardState.vaultId}
            onUpdate={(vaultId) =>
              props.setWizardState({ ...props.wizardState, vaultId })
            }
          />
          <CatalogLocation
            catalogAwsAccountId={props.wizardState.catalogAwsAccountId}
            catalogAwsRegion={props.wizardState.catalogAwsRegion}
            catalogIamRoleArn={props.wizardState.catalogIamRoleArn}
            onUpdate={(
              catalogAwsAccountId,
              catalogAwsRegion,
              catalogIamRoleArn
            ) =>
              props.setWizardState({
                ...props.wizardState,
                catalogAwsAccountId,
                catalogAwsRegion,
                catalogIamRoleArn,
              })
            }
          />
        </Stack>
        {/* right */}
        <ZeroEtlRouting
          state={props.wizardState}
          onChange={(updates) =>
            props.setWizardState((state) => ({ ...state, ...updates }))
          }
        />
      </Stack>
    </StepContainer>
  );
};

const StorageConfiguration = ({
  vaultId,
  onUpdate,
}: {
  vaultId: string;
  onUpdate: (vaultId: string) => void;
}) => {
  return (
    <Stack gap='24px'>
      <Typography variant='h3'>Iceberg table storage</Typography>
      <Typography variant='body1' color='text.secondary'>
        During backup, data from selected resources is transformed into Iceberg
        tables and kept in sync with the latest version in the vault.
      </Typography>
      <Stack direction='row' alignItems='center' gap='8px'>
        <Typography variant='subtitle1'>Select vault</Typography>
        <BackupVaultTagSelection
          vaultId={vaultId}
          onUpdate={(vault) => onUpdate(vault.id)}
          entityName='zero-etl'
          vaultsPredicate={(vault) =>
            vault.vaultAttributes.cloudProvider === Provider.Aws &&
            vault.isManagedByEon &&
            vault.status === 'ACTIVE'
          }
        />
      </Stack>
    </Stack>
  );
};

const CatalogLocation = ({
  catalogAwsAccountId,
  catalogAwsRegion,
  catalogIamRoleArn,
  onUpdate,
}: {
  catalogAwsAccountId: string;
  catalogAwsRegion: string;
  catalogIamRoleArn: string;
  onUpdate: (
    catalogAwsAccountId: string,
    catalogAwsRegion: string,
    catalogIamRoleArn: string
  ) => void;
}) => {
  return (
    <Stack gap='24px'>
      <Typography variant='h3'>Catalog location</Typography>
      <Typography variant='body2' color='text.secondary'>
        Select the AWS account ID and region. The AWS Glue Catalog is determined
        automatically, and your Iceberg tables will be available there.
      </Typography>

      <Stack gap='40px' divider={<Divider sx={{ borderStyle: 'dashed' }} />}>
        <Stack gap='12px'>
          <FormTextField
            title='AWS Account ID'
            placeholder={awsAccountIdConfig.placeholder}
            value={catalogAwsAccountId}
            validation={awsAccountIdConfig.validation}
            onChange={(e) =>
              onUpdate(e.target.value, catalogAwsRegion, catalogIamRoleArn)
            }
          />
          <AllRegionsSelector
            title='Region'
            provider={Provider.Aws}
            regionName={catalogAwsRegion}
            setRegionName={(regionName) =>
              onUpdate(catalogAwsAccountId, regionName, catalogIamRoleArn)
            }
          />
        </Stack>
        <Stack gap='24px'>
          <Typography variant='body2' color='text.secondary'>
            Provide a role for Eon to access and manage your catalog.
          </Typography>
          <FormTextField
            title='Catalog access role (ARN)'
            placeholder={roleArnConfig.placeholder}
            value={catalogIamRoleArn}
            validation={roleArnConfig.validation}
            onChange={(e) =>
              onUpdate(catalogAwsAccountId, catalogAwsRegion, e.target.value)
            }
          />
          <GetRequiredPoliciesButton />
        </Stack>
      </Stack>
    </Stack>
  );
};

export const GetRequiredPoliciesButton = () => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('permission');
  const dal = useDAL();
  const { user } = useUser();

  const scanningAccounts = dal.cloudAccounts.scanning.useList().data;
  const scanningAccountId =
    scanningAccounts?.accounts.find(
      (account) => account.cloudProvider === Provider.Aws
    )?.providerAccountId || '';

  const eonAccountId = user?.eonAccount.id ?? '';

  const permissionPolicyCode = JSON.stringify(
    {
      Version: '2012-10-17',
      Statement: [
        {
          Sid: 'EonGlueAccess',
          Effect: 'Allow',
          Action: [
            'glue:GetCatalog',
            'glue:GetDatabase',
            'glue:GetDatabases',
            'glue:CreateDatabase',
            'glue:UpdateDatabase',
            'glue:GetTable',
            'glue:GetTables',
            'glue:CreateTable',
            'glue:UpdateTable',
            'glue:GetPartition',
            'glue:GetPartitions',
            'glue:BatchGetPartition',
            'glue:CreatePartition',
            'glue:BatchCreatePartition',
            'glue:UpdatePartition',
            'glue:BatchUpdatePartition',
            'glue:DeletePartition',
            'glue:BatchDeletePartition',
          ],
          Resource: ['*'],
        },
      ],
    },
    null,
    2
  );

  const trustRelationshipCode = JSON.stringify(
    {
      Version: '2012-10-17',
      Statement: [
        {
          Effect: 'Allow',
          Principal: {
            AWS: [`arn:aws:iam::${scanningAccountId}:root`],
          },
          Action: 'sts:AssumeRole',
          Condition: {
            StringEquals: {
              'sts:ExternalId': eonAccountId,
            },
          },
        },
      ],
    },
    null,
    2
  );

  return (
    <>
      <TextLink href='' onClick={() => setOpen(true)}>
        Get required policies
      </TextLink>
      <MainDialog
        disableGutters
        isOpen={open}
        isLoading={false}
        header={{
          hideBorder: true,
          title: 'Get required policies',
          onClose: () => setOpen(false),
        }}
      >
        <Stack gap='24px' paddingBottom='24px' paddingTop='8px'>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', paddingX: '40px' }}
          >
            <Tab value='permission' label='Permission' />
            <Tab value='trust-relationship' label='Trust relationship' />
          </Tabs>
          <Stack gap='24px' padding='24px 40px'>
            <Typography variant='body2' color='text.secondary'>
              {activeTab === 'permission'
                ? 'Attach this policy to your IAM role to allow Eon to create and manage Iceberg tables in your AWS Glue Catalog.'
                : 'Update the trust relationship to allow Eon to assume this IAM role and access your AWS account securely.'}
            </Typography>
            <CopyableCode
              code={
                activeTab === 'permission'
                  ? permissionPolicyCode
                  : trustRelationshipCode
              }
              title='policy.json'
            />
          </Stack>
        </Stack>
      </MainDialog>
    </>
  );
};
